import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, Box, Typography, Alert, Divider } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUserPermissions } from '../../api/users';
import type { User, UserPermissions } from '../../types';

interface UserPermissionsProps {
  open: boolean;
  onClose: () => void;
  user: User;
}

export function UserPermissionsEditor({ open, onClose, user }: UserPermissionsProps) {
  const queryClient = useQueryClient();

  const currentPerms = (user.user_permissions as UserPermissions) || {
    modules: {},
    system: {
      can_manage_users: false,
      can_view_billing: false,
      can_manage_integrations: false,
      can_export_data: false,
      can_delete_records: false,
    },
  };

  const [permissions, setPermissions] = useState(currentPerms);

  const updateMutation = useMutation({
    mutationFn: (perms: UserPermissions) => updateUserPermissions(user.id, perms),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const handleSystemPermissionChange = (permission: keyof typeof permissions.system) => {
    setPermissions({
      ...permissions,
      system: {
        ...permissions.system,
        [permission]: !permissions.system[permission],
      },
    });
  };

  const handleSave = () => {
    updateMutation.mutate(permissions);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Permissions: {user.first_name} {user.last_name}
      </DialogTitle>
      <DialogContent>
        {updateMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to update permissions. Please try again.
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Role: <strong>{user.role}</strong>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <FormControl component="fieldset">
            <FormLabel component="legend">System Permissions</FormLabel>
            <FormGroup>
              <FormControlLabel control={<Checkbox checked={permissions.system.can_manage_users} onChange={() => handleSystemPermissionChange('can_manage_users')} />} label="Manage Users" />
              <FormControlLabel control={<Checkbox checked={permissions.system.can_view_billing} onChange={() => handleSystemPermissionChange('can_view_billing')} />} label="View Billing" />
              <FormControlLabel
                control={<Checkbox checked={permissions.system.can_manage_integrations} onChange={() => handleSystemPermissionChange('can_manage_integrations')} />}
                label="Manage Integrations"
              />
              <FormControlLabel control={<Checkbox checked={permissions.system.can_export_data} onChange={() => handleSystemPermissionChange('can_export_data')} />} label="Export Data" />
              <FormControlLabel control={<Checkbox checked={permissions.system.can_delete_records} onChange={() => handleSystemPermissionChange('can_delete_records')} />} label="Delete Records" />
            </FormGroup>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Note: Module-specific permissions coming soon
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Permissions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
